/**
 * Displays the header information for a log entry, including the username,
 * date/time, and a descriptive message about the log action.
 *
 * @param props - The component props
 * @param props.username - The name of the user who performed the action. Falls back to "Unknown User" if not provided.
 * @param props.date - The formatted date and time string to display
 * @param props.message - The log message content, which can include React nodes (e.g., formatted text with bold elements)
 *
 * @example
 * ```tsx
 * <LogHeader
 *   username="John Doe"
 *   date="2024-01-15 10:30 AM"
 *   message={<span>Modified <b>Product Name</b></span>}
 * />
 * ```
 */

import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ShowHideButtonSmall } from "../Buttons/Buttons";

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
  margin-top: ${({ theme }) => theme.resolve("spacing_xs")};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.resolve("spacing_xxs")};
  }
`;

const UserName = styled.span`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
`;

const ModifiedText = styled.span`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};

  b {
    font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  }
`;

const DateTime = styled.span`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  color: ${({ theme }) => theme.resolve("color_text_tertiary")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  white-space: nowrap;
`;

export function LogHeader({
  username,
  date,
  message,
  isExpanded,
  onToggle,
  showToggleButton = true,
  buttonText,
}: {
  username: string;
  date: string;
  message: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  showToggleButton?: boolean;
  buttonText?: string;
}) {
  const { t } = useTranslation();
  const buttonTxt = !buttonText
    ? isExpanded
      ? t("Hide Details")
      : t("Show Details")
    : buttonText;

  return (
    <HeaderContent>
      <FlexDiv>
        <UserName>{username ?? t("Unknown User")}</UserName>
        <DateTime>{date}</DateTime>
      </FlexDiv>
      <FlexDiv>
        <ModifiedText>{message}</ModifiedText>
        {showToggleButton && (
          <ShowHideButtonSmall onClick={onToggle} content={buttonTxt} />
        )}
      </FlexDiv>
    </HeaderContent>
  );
}
